import React, { useEffect, useState } from 'react';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { FaClipboardList, FaUserMd, FaEuroSign, FaPlus, FaSearch, FaArrowRight, FaClock, FaExclamationTriangle, FaEye, FaEyeSlash, FaChartLine } from 'react-icons/fa';
import { useNavigate, Link } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { getDeadlineAlert } from '../utils/deadlineUtils';
import { useSeasonalTheme } from '../contexts/SeasonalThemeContext';
import '../styles/Dashboard.css';

const Dashboard = () => {
    const { orders, clients, invoices, payments, creditNotes } = useData();
    const { user, profile } = useAuth();
    const { currentThemeId } = useSeasonalTheme();
    const isHalloween = currentThemeId === 'halloween';
    const HALLOWEEN_AVATARS = ['🎃', '💀', '😈', '🧙', '👻'];
    const navigate = useNavigate();
    const [isBirthday, setIsBirthday] = useState(false);
    const [hideFinancials, setHideFinancials] = useState(() => {
        return localStorage.getItem('enovadent_hide_financials') === 'true';
    });

    const toggleHideFinancials = (e) => {
        e.stopPropagation();
        setHideFinancials(prev => {
            const next = !prev;
            localStorage.setItem('enovadent_hide_financials', String(next));
            return next;
        });
    };

    useEffect(() => {
        if (profile?.birth_date) {
            const today = new Date();
            const birth = new Date(profile.birth_date);
            if (today.getDate() === birth.getDate() && today.getMonth() === birth.getMonth()) {
                setIsBirthday(true);
                const duration = 3 * 1000;
                const animationEnd = Date.now() + duration;
                const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

                const randomInRange = (min, max) => Math.random() * (max - min) + min;

                const interval = setInterval(function () {
                    const timeLeft = animationEnd - Date.now();

                    if (timeLeft <= 0) {
                        return clearInterval(interval);
                    }

                    const particleCount = 50 * (timeLeft / duration);
                    confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } }));
                    confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } }));
                }, 250);
            }
        }
    }, [profile]);

    const getGreeting = () => {
        if (!profile) return "Bonjour 👋";

        // Extract first name from full_name
        const fullName = profile.full_name || '';
        const firstName = fullName.split(' ')[0] || '';

        if (profile.role === 'admin') {
            // Use the first name from full_name, or fallback to 'Admin'
            if (firstName) {
                return `Bonjour ${firstName} 👋`;
            }
            return "Bonjour Admin 👋";
        }

        // Clients / Practitioners - use full name with "Dr."
        return `Bonjour Dr ${fullName || 'Client'} 👋`;
    };

    // Stats Calculations
    const activeOrders = orders.filter(o => o.status !== 'Livré' && o.status !== 'Facturé' && o.status !== 'Terminé').length;
    // Outstanding balance to collect (Reste à Encaisser) calculated dynamically
    const totalBilled = invoices.reduce((acc, inv) => acc + (parseFloat(inv.amount || inv.total_amount) || 0), 0);
    const totalPayments = (payments || []).reduce((sum, p) => {
        const amt = parseFloat(p.amount || 0);
        const isRefund = (p.payment_type || p.type || '').toLowerCase().includes('remboursement');
        return sum + (isRefund ? -amt : amt);
    }, 0);
    const totalCredits = (creditNotes || [])
        .filter(n => n.status !== 'pending')
        .reduce((sum, n) => sum + parseFloat(n.amount || 0), 0);
    const pendingInvoices = Math.max(0, totalBilled - totalPayments - totalCredits);
    const completedToday = orders.filter(o => {
        const today = new Date().toDateString();
        return new Date(o.createdAt).toDateString() === today; // Simplified 'recent' metric
    }).length;

    const urgentOrdersCount = orders.filter(o => getDeadlineAlert(o) !== null).length;
    const recentOrders = [...orders].sort((a, b) => {
        const alertA = getDeadlineAlert(a);
        const alertB = getDeadlineAlert(b);
        if (alertA && !alertB) return -1;
        if (!alertA && alertB) return 1;
        return new Date(b.createdAt) - new Date(a.createdAt);
    }).slice(0, 5);

    return (
        <div className="dashboard-page container-fluid px-0">
            {/* Welcome Banner */}
            <div className={`welcome-banner animate__animated animate__fadeInDown ${isBirthday ? 'bg-birthday' : ''}`} style={isBirthday ? { background: 'linear-gradient(135deg, #FFD700 0%, #FF69B4 100%)' } : {}}>
                <div className="welcome-text">
                    <h2 className="d-flex align-items-center">
                        {getGreeting()}
                        {isHalloween && (
                            <span className="halloween-skeleton-wave" title="Bouh ! Joyeux Halloween !">💀</span>
                        )}
                    </h2>
                    {isBirthday ? (
                        <p className="fw-bold fs-5 mt-2">🎉 ENOVADENT vous souhaite un joyeux anniversaire ! 🎂</p>
                    ) : (
                        <p>Voici un aperçu de votre activité aujourd'hui.</p>
                    )}
                </div>
                <div className="d-none d-md-block">
                    <p className={isBirthday ? "text-dark mb-0 font-monospace fw-bold" : "text-muted text-end mb-0 font-monospace"}>
                        {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                </div>
            </div>

            {/* Urgent Deadline Alert Banner */}
            {urgentOrdersCount > 0 && (
                <div 
                    className={`alert border-0 shadow-sm rounded-4 p-3 mb-4 d-flex align-items-center justify-content-between animate__animated animate__fadeIn ${isHalloween ? 'halloween-alert-banner' : 'alert-danger'}`} 
                    style={isHalloween ? {} : { background: '#fef2f2', borderLeft: '4px solid #ef4444' }}
                >
                    <div className="d-flex align-items-center gap-3">
                        <div className={isHalloween ? "text-dark fs-3" : "bg-danger text-white p-2 rounded-circle d-flex align-items-center justify-content-center"} style={isHalloween ? {} : { width: '38px', height: '38px' }}>
                            <FaExclamationTriangle size={isHalloween ? 22 : 18} />
                        </div>
                        <div>
                            <h6 className="fw-bold mb-0" style={isHalloween ? { color: '#2c1202' } : { color: '#dc2626' }}>
                                {isHalloween ? `Rappel Délais - ${urgentOrdersCount} cas en priorité (${urgentOrdersCount > 1 ? 'échéances proches' : 'échéance proche'})` : `Rappel Délais - ${urgentOrdersCount} cas en priorité (${urgentOrdersCount > 1 ? 'échéances proches' : 'échéance proche'})`}
                            </h6>
                            <p className="small mb-0" style={isHalloween ? { color: '#3d1a03' } : { color: '#6b7280' }}>
                                Ces cas en production arrivent à date de livraison dans moins de 48h ou accusent un retard.
                            </p>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        {isHalloween && (
                            <span className="halloween-lantern-badge" title="Lanterne d'Halloween">
                                🏮 {urgentOrdersCount}
                            </span>
                        )}
                        <Link to="/jobs" className={`btn btn-sm px-3 fw-bold rounded-3 ${isHalloween ? 'btn-dark text-warning border-0 shadow-sm' : 'btn-danger'}`}>
                            Voir les cas ({urgentOrdersCount})
                        </Link>
                    </div>
                </div>
            )}

            {/* Key Stats */}
            <div className="stats-grid">
                <div className="dashboard-stat-card clickable" onClick={() => navigate('/jobs')}>
                    {isHalloween ? (
                        <div className="halloween-pumpkin-icon">🎃</div>
                    ) : (
                        <div className="stat-icon-wrapper blue">
                            <FaClipboardList />
                        </div>
                    )}
                    <div className="stat-value">{activeOrders}</div>
                    <div className="stat-label">Travaux en cours</div>
                </div>

                <div className="dashboard-stat-card clickable" onClick={() => navigate('/clients')}>
                    {isHalloween ? (
                        <div className="halloween-pumpkin-icon">🎃</div>
                    ) : (
                        <div className="stat-icon-wrapper purple">
                            <FaUserMd />
                        </div>
                    )}
                    <div className="stat-value">{clients.length}</div>
                    <div className="stat-label">Praticiens Partenaires</div>
                </div>

                <div className="dashboard-stat-card clickable" onClick={() => navigate('/jobs')}>
                    {isHalloween ? (
                        <div className="halloween-pumpkin-icon">🎃</div>
                    ) : (
                        <div className="stat-icon-wrapper orange">
                            <FaClock />
                        </div>
                    )}
                    <div className="stat-value">{completedToday}</div>
                    <div className="stat-label">Reçus Aujourd'hui</div>
                </div>

                <div className="dashboard-stat-card clickable" onClick={() => navigate('/accounting?tab=analytics')} title="Cliquer pour analyser l'évolution du chiffre d'affaires">
                    <div className="d-flex justify-content-between align-items-start" style={{ marginBottom: '16px' }}>
                        {isHalloween ? (
                            <div className="halloween-pumpkin-icon mb-0">🎃</div>
                        ) : (
                            <div className="stat-icon-wrapper green mb-0">
                                <FaEuroSign />
                            </div>
                        )}
                        <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-light text-primary border small" style={{ fontSize: '0.72rem' }}>
                                <FaChartLine className="me-1" /> Analyser ↗
                            </span>
                            <button
                                type="button"
                                className="financial-privacy-btn"
                                onClick={toggleHideFinancials}
                                title={hideFinancials ? "Afficher le montant" : "Masquer / Flouter le montant"}
                            >
                                {hideFinancials ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                            </button>
                        </div>
                    </div>
                    <div className={`stat-value ${hideFinancials ? 'stat-value-blurred' : ''}`}>
                        {hideFinancials ? '•••••• €' : `${totalBilled.toFixed(2)} €`}
                    </div>
                    <div className="stat-label">Chiffre d'Affaires</div>
                    <div className="text-muted small mt-1">
                        Reste à encaisser : <span className={`fw-semibold text-dark ${hideFinancials ? 'stat-value-blurred' : ''}`}>{hideFinancials ? '••• €' : `${pendingInvoices.toFixed(2)} €`}</span>
                    </div>
                </div>
            </div>

            <div className="row g-4">
                {/* Recent Activity Feed */}
                <div className="col-lg-8">
                    <h3 className="dashboard-section-title">
                        <FaClipboardList className="text-primary" /> Derniers Travaux
                    </h3>
                    <div className="activity-card">
                        {recentOrders.length === 0 ? (
                            <div className="p-5 text-center text-muted">Aucune activité récente.</div>
                        ) : (
                            recentOrders.map((order, idx) => {
                                const deadline = getDeadlineAlert(order);
                                return (
                                    <div
                                        className="activity-item"
                                        key={order.id}
                                        onClick={() => navigate(`/jobs/${order.id}`)}
                                        style={{ cursor: 'pointer', background: deadline ? '#fffdfd' : 'transparent', borderLeft: deadline ? '4px solid #ef4444' : 'none' }}
                                    >
                                        <div className="activity-info">
                                            <div className="activity-avatar" style={isHalloween ? { background: '#fef3c7', fontSize: '1.25rem', border: '1px solid #fde68a' } : {}}>
                                                {isHalloween 
                                                    ? HALLOWEEN_AVATARS[idx % HALLOWEEN_AVATARS.length] 
                                                    : (order.patientName || 'P').charAt(0).toUpperCase()}
                                            </div>
                                            <div className="activity-details">
                                                <h6>{order.patientName || 'Patient inconnu'}</h6>
                                                <p>Dr. {order.practitionerName || 'Inconnu'} • <span className="font-monospace">#{order.caseNumber || (order.id || '').substr(-6)}</span></p>
                                            </div>
                                        </div>
                                        <div className="activity-meta text-end">
                                            <div className="d-flex align-items-center justify-content-end gap-2">
                                                {deadline && (
                                                    <span className={`deadline-badge-pill ${deadline.badgeClass}`}>
                                                        <FaExclamationTriangle size={10} className="me-1" />
                                                        {deadline.shortLabel}
                                                    </span>
                                                )}
                                                <span className={`activity-status ${(order.status || 'reçu').toLowerCase()}`}>
                                                    {order.status || 'Reçu'}
                                                </span>
                                            </div>
                                            <div className="text-muted small mt-1">
                                                Livraison : {order.appointmentDate ? new Date(order.appointmentDate).toLocaleDateString('fr-FR') : '-'}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                        <div className="p-3 text-center border-top">
                            <Link to="/jobs" className="btn btn-link text-decoration-none fw-bold">
                                Voir tous les travaux <FaArrowRight className="ms-1" />
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Quick Actions */}
                <div className="col-lg-4">
                    <h3 className="dashboard-section-title">
                        Accès Rapide
                    </h3>
                    <div className="quick-actions-grid">
                        <Link to="/jobs/new" className="quick-action-btn">
                            {isHalloween ? <span className="halloween-qa-icon">☠️</span> : <FaPlus className="quick-action-icon" />}
                            <h5>Nouveau Travail</h5>
                        </Link>

                        <Link to="/clients" className="quick-action-btn">
                            {isHalloween ? <span className="halloween-qa-icon">🧙‍♀️</span> : <FaUserMd className="quick-action-icon text-purple" style={{ color: '#8b5cf6' }} />}
                            <h5>Mes Praticiens</h5>
                        </Link>

                        <Link to="/accounting?tab=analytics" className="quick-action-btn">
                            {isHalloween ? <span className="halloween-qa-icon">🦇</span> : <FaChartLine className="quick-action-icon text-primary" style={{ color: '#0ea5e9' }} />}
                            <h5>Évolution CA</h5>
                        </Link>

                        <Link to="/accounting" className="quick-action-btn">
                            {isHalloween ? <span className="halloween-qa-icon">🪙</span> : <FaEuroSign className="quick-action-icon text-green" style={{ color: '#10b981' }} />}
                            <h5>Comptabilité</h5>
                        </Link>

                        <Link to="/jobs" className="quick-action-btn">
                            {isHalloween ? <span className="halloween-qa-icon">🔍</span> : <FaSearch className="quick-action-icon text-orange" style={{ color: '#f59e0b' }} />}
                            <h5>Rechercher</h5>
                        </Link>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default Dashboard;
